import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { faToggleOn } from '@fortawesome/pro-regular-svg-icons/faToggleOn';
import {
  faArrowDown, faArrowUp, faBan, faCheck, faFileArrowDown, faLockAlt, faSignOut, faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import {
  ContextMenuDefaultItem, ContextMenuItemCoreComponent,
} from '~/_shared/baseComponents/contextMenu';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  canMemberManageClient, isClientAdmin, isClientOwner,
} from '~/clientTeamManagement/memberRoles.helpers';
import {
  type MemberRole, MemberRoles,
} from '../memberRole.type';
import { getTeamMemberLimitProhibitsAdd } from '../teamManagement.helpers';
import {
  MemberStatus, type TeamMemberLimitsData,
} from '../teamManagement.repository';
import { type InvitationAndMemberData } from '../teamManagementModal.container';

type CurrentMemberData = Readonly<{
  id: number | null;
  role: MemberRole;
}>;

type TeamManagementMenuComponentProps = {
  currentMemberData: CurrentMemberData;
  isClientMigrationInProgress: boolean;
  isLoading: boolean;
  member: InvitationAndMemberData;
  passwordResetEmailSent: boolean;
  limits: TeamMemberLimitsData;

  allowPublicExport: () => void;
  disallowPublicExport: () => void;
  onChangeRole: (role: MemberRole) => void;
  onDeleteUser: () => void;
  onEnableOrDisableUser: () => void;
  onLoginAsUser: () => void;
  onRevokeInvite: () => void;
  onSentPasswordRecoveryEmail: () => void;
};

export const TeamManagementMenuComponent = ({
  allowPublicExport,
  disallowPublicExport,
  currentMemberData,
  isClientMigrationInProgress,
  isLoading,
  member,
  passwordResetEmailSent,
  onLoginAsUser,
  onSentPasswordRecoveryEmail,
  onEnableOrDisableUser,
  onDeleteUser,
  onChangeRole,
  onRevokeInvite,
  limits,
}: TeamManagementMenuComponentProps) => {
  const [t] = useTranslation();

  const isMemberCurrentUser = currentMemberData.id === member.id;
  const isMemberEnabled = member.status === MemberStatus.ENABLED;
  const isAtMembersLimit = getTeamMemberLimitProhibitsAdd(limits);
  const isMemberSf = member.isSalesforceOrigin || member.hasSalesforceIntegration;
  const loginAsUserAllowed = !isMemberCurrentUser && !isClientOwner(member) && (canMemberManageClient(currentMemberData.role));
  const passwordResetAllowed = !isClientMigrationInProgress && isMemberEnabled && !passwordResetEmailSent;
  const demotionAllowed = !isClientMigrationInProgress && !isMemberCurrentUser && !isClientOwner(member) && isClientAdmin(member) && canMemberManageClient(currentMemberData.role);
  const promotionToAdminAllowed = !isClientMigrationInProgress && member.role === MemberRoles.INDEPENDENT_MEMBER;
  const isPasswordResetSent = isMemberEnabled && passwordResetEmailSent;
  const promotionToOwnerAllowed = !isClientMigrationInProgress && isMemberEnabled && isClientAdmin(member) && isClientOwner(currentMemberData);
  const deletionAllowed = !isClientMigrationInProgress && !isMemberCurrentUser && canMemberManageClient(currentMemberData.role) && !isClientOwner(member);
  const statusChangeAllowed = !isClientMigrationInProgress && (isMemberEnabled || (!isMemberEnabled && !isAtMembersLimit)) && !isMemberCurrentUser && canMemberManageClient(currentMemberData.role) && !isClientOwner(member);
  const isChangePublicExportAllowed = isMemberEnabled && canMemberManageClient(currentMemberData.role);
  const isAllowPublicExportEnabled = isChangePublicExportAllowed && !member.allowPublicExportAuthorization;
  const isDisallowPublicExportEnabled = isChangePublicExportAllowed && member.allowPublicExportAuthorization;
  const deletionDisabledReason = isMemberSf ? t('salesforce.memberActions.deletionDisabledReason') : undefined;
  const statusChangeDisabledReason = isMemberSf ? t('salesforce.memberActions.statusChangeDisabledReason') : undefined;

  const actions: { icon: IconProp; onClick?: () => void; text: string; isDestructive?: boolean; isDisabled?: boolean; tooltipLabel?: string }[] = [
    ...(loginAsUserAllowed ? [{ icon: faSignOut, onClick: onLoginAsUser, text: t('Login as User') }] : []),
    ...(passwordResetAllowed ? [{ icon: faLockAlt, onClick: onSentPasswordRecoveryEmail, text: t('Send Password Reset') }] : []),
    ...(isPasswordResetSent ? [{ icon: faCheck, text: t('Password Reset Sent!') }] : []),
    ...(promotionToOwnerAllowed ? [{ icon: faArrowUp, onClick: () => onChangeRole(MemberRoles.COMPANY_OWNER), text: t('Make New Account Owner') }] : []),
    ...(promotionToAdminAllowed ? [{ icon: faArrowUp, onClick: () => onChangeRole(MemberRoles.COMPANY_ADMIN), text: t('Promote to Admin') }] : []),
    ...(demotionAllowed ? [{ icon: faArrowDown, onClick: () => onChangeRole(MemberRoles.INDEPENDENT_MEMBER), text: t('Demote to Member') }] : []),
    ...(isAllowPublicExportEnabled ? [{ icon: faFileArrowDown, onClick: allowPublicExport, text: t('userManagement.memberActions.allowPublicExport') }] : []),
    ...(isDisallowPublicExportEnabled ? [{ icon: faFileArrowDown, onClick: disallowPublicExport, isDestructive: true, text: t('userManagement.memberActions.disallowPublicExport') }] : []),
    ...(statusChangeAllowed ? [{ icon: faToggleOn, onClick: onEnableOrDisableUser, text: isMemberEnabled ? t('Disable User') : t('Enable User'), isDisabled: !!statusChangeDisabledReason, tooltipLabel: statusChangeDisabledReason }] : []),
    ...(deletionAllowed ? [{ icon: faTrash, onClick: onDeleteUser, text: t('Delete User'), isDestructive: true, isDisabled: !!deletionDisabledReason, tooltipLabel: deletionDisabledReason }] : []),
  ];

  return (
    <>
      {isLoading && (
        <OverlayLoaderComponent
          loaderSize={45}
        />
      )}
      {member.id ? (
        actions.map((action, index) => (
          <ContextMenuItemCoreComponent key={index}>
            <ContextMenuDefaultItem
              icon={action.icon}
              onClick={action.onClick}
              isDestructive={action.isDestructive}
              isDisabled={action.isDisabled}
              tooltipLabel={action.tooltipLabel}
            >
              {action.text}
            </ContextMenuDefaultItem>
          </ContextMenuItemCoreComponent>
        ))
      ) : (
        <ContextMenuItemCoreComponent>
          <ContextMenuDefaultItem
            icon={faBan}
            onClick={onRevokeInvite}
          >
            {t('Revoke Invite')}
          </ContextMenuDefaultItem>
        </ContextMenuItemCoreComponent>
      )}
    </>
  );
};
